import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FormErrorMessage } from "../../components";
import { formatUSPhoneNumber, isValidUSPhoneNumber, normalizeUSPhoneNumberToE164 } from "../../utils/phoneNumberFormatting";
import { useFunctionalityAuthorizeByPhone, useFunctionalityCompanyNames } from "../../pages/hooks";
import { SelectModel, VrPhoneAssignmentResponse } from "../../models";
import Select from "react-select";
import { UtilsJQuery } from "../../utils";

type FormValues = {
  companyId: string;
  userId: string;
  phoneNumber: string;
};

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  onSave?: (form: FormValues) => void;
  editAssignment?: VrPhoneAssignmentResponse | null;
};

export function AuthorizeByPhoneModal(props: Props): JSX.Element {
  const [companyNames, setCompanyNames] = useState<SelectModel[]>();
  const [selectedCompany, setSelectedCompany] = useState<SelectModel[]>([]);
  const [userOptions, setUserOptions] = useState<SelectModel[]>([]);
  const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const isEditMode = Boolean(props.editAssignment);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      companyId: "",
      userId: "",
      phoneNumber: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { onCompanyNames } = useFunctionalityCompanyNames(true);
  const {
    onGetTinnedUsersByCompany,
    onUpsertPhoneAssignment,
    onCheckPhoneAssignmentExists,
  } = useFunctionalityAuthorizeByPhone();

  const phoneNumberValue = watch("phoneNumber");
  const selectedCompanyId = watch("companyId");
  const isUserLocked = !selectedCompanyId || isEditMode;
  const [phoneExistsMsg, setPhoneExistsMsg] = useState("");
  const phoneNumberRegister = register("phoneNumber", {
    required: true,
    validate: (value) => isValidUSPhoneNumber(value) || "Invalid US phone number",
  });

  const resetModal = () => {
    reset({
      companyId: "",
      userId: "",
      phoneNumber: "",
    });
    setSelectedCompany([]);
    setUserOptions([]);
    setHasLoadedUsers(false);
    setIsUsersLoading(false);
    setPhoneExistsMsg("");
  };

  const onClose = () => {
    resetModal();
    props.handleShow(false);
  };

  const checkPhoneAssignmentExists = async (rawValue: string) => {
    const normalized = normalizeUSPhoneNumberToE164(rawValue);
    if (!normalized || !isValidUSPhoneNumber(normalized)) {
      setPhoneExistsMsg("");
      return false;
    }
    try {
      const res = await onCheckPhoneAssignmentExists(
        normalized,
        props.editAssignment?.id
      );
      const exists = Boolean((res as any)?.exists ?? res?.data?.exists);
      if (exists) {
        setPhoneExistsMsg("This phone number is already assigned.");
        return true;
      }
      setPhoneExistsMsg("");
      return false;
    } catch (error) {
      console.error(error);
      setPhoneExistsMsg("");
      return false;
    }
  };

  const onSubmit = async (form: FormValues) => {
    const btnLoading = UtilsJQuery.Ladda(".btn-phone-authorization-save");
    btnLoading.start?.();
    try {
      const exists = await checkPhoneAssignmentExists(form.phoneNumber);
      if (exists) {
        return;
      }
      const res = await onUpsertPhoneAssignment({
        companyId: form.companyId,
        userId: form.userId,
        phoneNumber: form.phoneNumber,
        isEnabled: props.editAssignment?.isEnabled ?? true,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "Failed to save phone authorization");
        return;
      }
      toast.success(isEditMode ? "Phone authorization updated" : "Phone authorization created");
      resetModal();
      props.onSave?.(form);
      props.handleShow(false);
    } catch (error) {
      console.error(error);
      toast.error(isEditMode ? "Failed to update phone authorization" : "Failed to create phone authorization");
    } finally {
      btnLoading.stop?.();
    }
  };

  useEffect(() => {
    onCompanyNames("").then((res) => {
      setCompanyNames([
        {
          value: "",
          label: "Any",
        },
        ...(res?.data ?? []),
      ]);
    });
  }, [onCompanyNames]);

  const mapUserOptions = (payload: any): SelectModel[] => {
    const rawList = Array.isArray(payload) ? payload : payload?.data;
    const list = Array.isArray(rawList) ? rawList : rawList?.data;
    if (!Array.isArray(list)) {
      return [];
    }

    return list.map((item) => {
      const name = item?.fullName ?? item?.email ?? item?.name ?? "User";
      const tin = item?.tinNumber ?? "";
      const label = tin ? `${name} (${tin})` : name;
      const value = item?.userId ?? item?.id ?? "";

      return { label, value };
    });
  };

  const loadUsersForCompany = (companyId: string, selectedUserId?: string) => {
    setUserOptions([]);
    setHasLoadedUsers(false);
    setIsUsersLoading(true);
    if (isEditMode) {
      setIsUsersLoading(false);
      setHasLoadedUsers(true);
      return;
    }
    if (!companyId) {
      setIsUsersLoading(false);
      return;
    }
    onGetTinnedUsersByCompany(companyId)
      .then((res) => {
        const options = mapUserOptions(res);
        const editUserId = props.editAssignment?.userId;
        const editUserLabel =
          props.editAssignment?.userFullName ??
          props.editAssignment?.userEmail ??
          "Selected user";
        const shouldPrepend =
          props.editAssignment?.companyId === companyId &&
          editUserId &&
          !options.some((option) => option.value === editUserId);
        const nextOptions = shouldPrepend
          ? [{ value: editUserId, label: editUserLabel }, ...options]
          : options;
        setUserOptions(nextOptions);
        if (selectedUserId) {
          setValue("userId", selectedUserId, { shouldValidate: false, shouldDirty: true });
        }
        setHasLoadedUsers(true);
      })
      .catch(() => {
        setUserOptions([]);
        setHasLoadedUsers(true);
      })
      .finally(() => {
        setIsUsersLoading(false);
      });
  };

  useEffect(() => {
    if (!props.show || !props.editAssignment) {
      return;
    }
    const assignment = props.editAssignment;
    const companyId = assignment.companyId ?? "";
    const userId = assignment.userId ?? "";
    const phoneNumber = assignment.phoneNumberNormalized ?? assignment.phoneNumberRaw ?? "";

    reset({
      companyId,
      userId,
      phoneNumber,
    });
    setSelectedCompany([
      {
        value: companyId,
        label: assignment.companyName ?? "Company",
      },
    ]);
    setUserOptions([
      {
        value: userId,
        label: assignment.userFullName ?? assignment.userEmail ?? "User",
      },
    ]);
    setHasLoadedUsers(true);
    setIsUsersLoading(false);
  }, [props.show, props.editAssignment, reset]);

  return (
    <Modal show={props.show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? "Edit Phone Authorization" : "Add Phone Authorization"}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <div className="form-group">
                    <label htmlFor="authorizeCompany" className="position-relative required">
                      Company
                    </label>
                    <Select
                      options={companyNames ?? []}
                      value={selectedCompany}
                      isDisabled={isEditMode}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select a company"
                      onChange={(value) => {
                        if (isEditMode) {
                          return;
                        }
                        const companyId = value?.value ?? "";
                        setValue("companyId", companyId, {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                        setValue("userId", "", {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                        setSelectedCompany(
                          value ? [{ label: value.label, value: value.value }] : []
                        );
                        loadUsersForCompany(companyId);
                      }}
                    />
                    <input type="hidden" {...register("companyId", { required: true })} />
                    {errors.companyId && <FormErrorMessage message="required" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1" />
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <div className="form-group">
                    <label htmlFor="authorizeUser" className="position-relative required">
                      User
                    </label>
                    <select
                      id="authorizeUser"
                      className="form-control"
                      disabled={isUserLocked || isUsersLoading}
                      aria-disabled={isUserLocked || isUsersLoading}
                      {...register("userId", { required: true })}
                    >
                      {isUsersLoading ? (
                        <option value="" disabled>
                          Loading users...
                        </option>
                      ) : userOptions.length === 0 && hasLoadedUsers ? (
                        <option value="" disabled>
                          There are no tinned users under this company
                        </option>
                      ) : (
                        <option value="" disabled>
                          Select user
                        </option>
                      )}
                      {userOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.userId && <FormErrorMessage message="required" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="authorizePhone" className="position-relative required">
                  Phone Number
                </label>
                <input
                  id="authorizePhone"
                  type="text"
                  className="form-control"
                  placeholder="+1 (555) 123-4567"
                  value={phoneNumberValue}
                  {...phoneNumberRegister}
                  onBlur={(event) => {
                    phoneNumberRegister.onBlur(event);
                    const formatted = formatUSPhoneNumber(event.target.value);
                    setValue("phoneNumber", formatted, { shouldValidate: true });
                    checkPhoneAssignmentExists(formatted || event.target.value);
                  }}
                />
                {phoneExistsMsg && <FormErrorMessage message={phoneExistsMsg} />}
                {errors.phoneNumber && (
                  <FormErrorMessage
                    message={(errors.phoneNumber.message as string) || "required"}
                  />
                )}
                {isEditMode ? (
                  <div className="col-12 mt-2 pl-1">
                    <sup>
                      To assign the phone number to another user, delete the entry and create a new one.
                    </sup>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary text-uppercase" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="btn btn-primary text-uppercase btn-phone-authorization-save">
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
